import React from "react";
import { SEO } from "@/components/common/SEO";
import { COMPANY } from "@/i18n/translations";
import { CleaningLeadForm } from "@/components/common/CleaningLeadForm";
import { BeforeAfterSlider } from "@/components/common/BeforeAfterSlider";
import {
  CheckCircle2,
  Phone,
  ShieldCheck,
  Sparkles,
  Star,
  Clock,
  MapPin,
  Award,
  Users,
  ArrowRight,
} from "lucide-react";

const TRUST_BADGES = [
  { Icon: ShieldCheck, label: "DM Licensed" },
  { Icon: Users, label: "Vetted Cleaners" },
  { Icon: Award, label: "100% Guarantee" },
  { Icon: Clock, label: "Same-Day Service" },
];

const REVIEWS = [
  {
    quote:
      "Booked an Orion deep clean for our move-out. They went into every corner — got our full deposit back. Worth every dirham.",
    name: "Sarah K.",
    area: "Dubai Marina",
    rating: 5,
  },
  {
    quote:
      "Office of 30 staff. Switched to Orion 6 months ago. Cleaner office, lower bill, one supervisor instead of three vendors.",
    name: "Rohan M.",
    area: "Business Bay",
    rating: 5,
  },
  {
    quote:
      "Showed up exactly on time, in uniform, with all their own equipment. My villa hasn't looked this good since handover.",
    name: "Aisha R.",
    area: "Arabian Ranches",
    rating: 5,
  },
];

const AREAS = [
  "Dubai Marina", "JLT", "Downtown", "Business Bay", "DIFC",
  "Palm Jumeirah", "JBR", "Mirdif", "Al Khawaneej", "Al Barsha",
  "Arabian Ranches", "Emirates Hills", "Meadows", "JVC", "JVT",
  "Damac Hills", "Dubai Hills", "Silicon Oasis", "Jumeirah",
  "Umm Suqeim", "Deira", "Bur Dubai", "Karama", "Al Quoz",
];

const PROCESS = [
  { n: "01", title: "You book in 60 seconds", desc: "Fill the form or WhatsApp us. We confirm price and slot." },
  { n: "02", title: "We arrive on time", desc: "Uniformed, ID-badged team with all equipment and chemicals." },
  { n: "03", title: "Deep clean delivered", desc: "Every corner, every surface, photo report on completion." },
  { n: "04", title: "Satisfaction guaranteed", desc: "Not happy with anything? We come back free." },
];

const SERVICES = [
  { title: "Deep Cleaning", desc: "Top-to-bottom reset of your space using validated chemicals." },
  { title: "Move-In / Move-Out", desc: "Landlord-checklist-aligned cleans that get your deposit back." },
  { title: "Regular Cleaning", desc: "Weekly or monthly housekeeping for villas and apartments." },
  { title: "Office Cleaning", desc: "Daily or off-hours cleaning for offices and co-working spaces." },
  { title: "Post-Construction", desc: "Builder's dust, grout haze and paint splash removed." },
  { title: "Sofa, Carpet & Mattress", desc: "Hot-water extraction and shampoo treatment for upholstery." },
];

const FAQS = [
  {
    q: "How much does a deep cleaning cost in Dubai?",
    a: "Studio apartments start from AED 350, 1-bed AED 550, 2-bed AED 750, villas from AED 1,500. We give you a fixed price before we start.",
  },
  {
    q: "How quickly can you come?",
    a: "Same day is usually possible. Worst case, next-day morning slot. Book in the morning and our team can be at your door by afternoon.",
  },
  {
    q: "Are your cleaners trained and insured?",
    a: "Yes. All Orion cleaners are sponsored by us, DM-licensed, uniformed, ID-badged and covered by public liability insurance.",
  },
  {
    q: "Do you bring equipment and chemicals?",
    a: "Yes. Vacuums, steamers, scrubbers, ladders and all DM-approved chemicals are included in the price. You don't need to provide anything.",
  },
  {
    q: "Is the cleaning safe for kids and pets?",
    a: "Yes. We use eco-friendly chemicals on request. Just mention sensitivities when booking and we'll match the chemistry to your home.",
  },
  {
    q: "What if I'm not happy with the result?",
    a: "We come back free. If anything in your home isn't to standard within 48 hours of the clean, we send a team back at no cost.",
  },
];

export default function CleaningServicesDubai() {
  const serviceLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Cleaning Services Dubai",
    provider: {
      "@type": "LocalBusiness",
      name: COMPANY.brandFull,
      telephone: COMPANY.phone,
      email: COMPANY.email,
      url: `https://${COMPANY.domain}`,
      areaServed: { "@type": "City", name: "Dubai" },
    },
    areaServed: "Dubai",
    description:
      "Professional cleaning services in Dubai — deep cleaning, move-in/move-out, office and post-construction cleaning. Same-day bookings, DM-licensed teams.",
    serviceType: "Cleaning Services",
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div data-testid="ads-cleaning-services-dubai">
      <SEO
        title="Cleaning Services in Dubai | Same-Day Booking | Orion FM"
        description="Trusted cleaning services in Dubai — deep cleaning, move-in/move-out, office and villa cleaning. DM-licensed teams, same-day bookings, 100% satisfaction guarantee."
        jsonLd={serviceLd}
      />
      <SEO jsonLd={faqLd} jsonLdId="ads-cleaning-faq-jsonld" />

      {/* Hero with inline lead form */}
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 opacity-25">
          <img
            src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000"
            alt="Cleaning services Dubai"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-950/90 to-blue-950/60" />
        </div>
        <div className="container-orion relative py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-blue-300 border border-blue-400/40 rounded-full px-3 py-1.5">
                <MapPin className="w-3.5 h-3.5" />
                Dubai-wide · Same-Day
              </div>
              <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium leading-[1.02]">
                Cleaning Services in Dubai — Done Right, the First Time.
              </h1>
              <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-xl">
                Deep cleaning, move-in/out, office cleaning and post-construction handled by
                DM-licensed teams. Fixed pricing, same-day booking, 100% satisfaction guarantee.
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#book"
                  data-testid="ads-hero-cta-book"
                  className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-7 py-3.5 text-sm font-semibold text-white transition-colors"
                >
                  Get Your Free Quote
                  <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href={`tel:${COMPANY.phone}`}
                  data-testid="ads-hero-cta-call"
                  className="inline-flex items-center gap-2 rounded-full border border-white/30 hover:bg-white/10 px-7 py-3.5 text-sm font-semibold text-white transition-colors"
                >
                  <Phone className="w-4 h-4" />
                  {COMPANY.phoneDisplay}
                </a>
              </div>

              <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4">
                {TRUST_BADGES.map(({ Icon, label }, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-sm text-slate-200 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5"
                  >
                    <Icon className="w-4 h-4 text-blue-300" />
                    {label}
                  </div>
                ))}
              </div>

              <div className="mt-6 flex items-center gap-3 text-sm text-slate-300">
                <div className="flex">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star key={s} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span>4.9 · 200+ verified Dubai reviews</span>
              </div>
            </div>

            <div id="book" className="lg:col-span-5">
              <div className="bg-white rounded-3xl p-7 shadow-2xl shadow-blue-900/40">
                <div className="text-eyebrow text-blue-700">Free quote · 30-min callback</div>
                <h2 className="mt-2 font-heading text-2xl font-medium text-slate-900">
                  Book your cleaning
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  Tell us a few details. We&apos;ll call within 30 minutes.
                </p>
                <div className="mt-5">
                  <CleaningLeadForm pageSource="google-ads-cleaning-dubai" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Before / After */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container-orion">
          <div className="max-w-3xl mb-10">
            <div className="text-eyebrow text-blue-700">See the difference</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-tight">
              Drag the slider. Before → After.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Real Dubai homes, real Orion clean. No filters, no staging.
            </p>
          </div>
          <BeforeAfterSlider
            beforeSrc="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            afterSrc="https://images.unsplash.com/photo-1497366216548-37526070297c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            beforeLabel="Before"
            afterLabel="After Orion clean"
            aspectClass="aspect-[16/9]"
          />
        </div>
      </section>

      {/* Services */}
      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="container-orion">
          <div className="max-w-3xl">
            <div className="text-eyebrow text-blue-700">What we clean</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-tight">
              Everything from a 1-bed apartment to a 50,000 sqft office.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {SERVICES.map((s, i) => (
              <div
                key={i}
                data-testid={`ads-service-card-${i}`}
                className="bg-white border border-slate-200 rounded-2xl p-7 card-hover"
              >
                <div className="w-11 h-11 rounded-lg bg-blue-50 text-blue-700 flex items-center justify-center">
                  <Sparkles className="w-5 h-5" />
                </div>
                <h3 className="mt-5 font-heading text-lg font-medium text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="container-orion">
          <div className="max-w-3xl">
            <div className="text-eyebrow text-blue-700">How it works</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-tight">
              Four steps from booking to spotless.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROCESS.map((p, i) => (
              <div
                key={i}
                className="relative bg-slate-50 border border-slate-200 rounded-2xl p-7"
              >
                <div className="font-heading text-4xl font-medium text-blue-700">{p.n}</div>
                <h3 className="mt-4 font-heading text-lg font-medium text-slate-900">{p.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="container-orion">
          <div className="max-w-3xl">
            <div className="text-eyebrow text-blue-700">Verified reviews</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium text-slate-900 leading-tight">
              What Dubai clients say.
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {REVIEWS.map((r, i) => (
              <div
                key={i}
                data-testid={`ads-review-${i}`}
                className="bg-white border border-slate-200 rounded-2xl p-7"
              >
                <div className="flex">
                  {Array.from({ length: r.rating }).map((_, idx) => (
                    <Star key={idx} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="mt-4 text-slate-700 leading-relaxed">&ldquo;{r.quote}&rdquo;</p>
                <div className="mt-5 text-sm text-slate-500">
                  <span className="font-medium text-slate-900">{r.name}</span> · {r.area}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Area coverage */}
      <section className="py-16 bg-white">
        <div className="container-orion">
          <div className="max-w-3xl">
            <div className="text-eyebrow text-blue-700">Coverage</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 leading-tight">
              We cover all of Dubai.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Same-day mobilization across these neighbourhoods (and everywhere in between).
            </p>
          </div>
          <div className="mt-8 flex flex-wrap gap-2">
            {AREAS.map((a) => (
              <span
                key={a}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-slate-100 text-sm text-slate-700"
              >
                <MapPin className="w-3 h-3 text-blue-700" />
                {a}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 lg:py-24 bg-slate-50">
        <div className="container-orion grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="text-eyebrow text-blue-700">Frequently asked</div>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 leading-tight">
              Honest answers to common questions.
            </h2>
            <p className="mt-5 text-slate-600 leading-relaxed">
              Still have a question?{" "}
              <a
                href={`https://wa.me/${COMPANY.whatsapp}`}
                target="_blank"
                rel="noreferrer"
                className="text-blue-700 underline hover:no-underline"
              >
                WhatsApp our team
              </a>
              .
            </p>
          </div>
          <div className="lg:col-span-7 space-y-4">
            {FAQS.map((f, i) => (
              <details
                key={i}
                data-testid={`ads-faq-${i}`}
                className="group bg-white border border-slate-200 rounded-2xl p-5 [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <span className="font-heading text-base font-medium text-slate-900">{f.q}</span>
                  <span className="ml-4 text-blue-700 transition-transform group-open:rotate-45 text-2xl leading-none">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-slate-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 lg:py-24 bg-slate-950 text-white">
        <div className="container-orion text-center max-w-2xl mx-auto">
          <div className="text-eyebrow text-blue-300">Ready to book?</div>
          <h2 className="mt-4 font-heading text-4xl sm:text-5xl tracking-tighter font-medium leading-tight">
            Spotless home or office.<br />Same-day. Guaranteed.
          </h2>
          <p className="mt-6 text-slate-300 leading-relaxed">
            Pick a slot, share the basics, we&apos;ll call you in 30 minutes with a fixed price.
          </p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <a
              href="#book"
              data-testid="ads-final-cta-book"
              className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-500 px-7 py-3.5 text-sm font-semibold text-white transition-colors"
            >
              Get My Free Quote
              <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href={`tel:${COMPANY.phone}`}
              data-testid="ads-final-cta-call"
              className="inline-flex items-center gap-2 rounded-full border border-white/30 hover:bg-white/10 px-7 py-3.5 text-sm font-semibold text-white transition-colors"
            >
              <Phone className="w-4 h-4" />
              Call {COMPANY.phoneDisplay}
            </a>
          </div>
          <div className="mt-10 flex items-center justify-center gap-3 text-sm text-slate-400">
            <CheckCircle2 className="w-4 h-4 text-blue-400" />
            DM Licensed · Insured · 100% satisfaction guarantee
          </div>
        </div>
      </section>
    </div>
  );
}
